import type { AbortReason, RobotPosition, Screen, Ward } from './types';

export const PIN_LENGTH = 4;

/** Lamp outputs on a Shelly Pro 4PM (O1–O4), until the server says what this unit has wired. */
export const DEFAULT_LAMP_CHANNELS: readonly number[] = [0, 1, 2, 3];

/** Channel 0 is "Bulb 1" — operators count from one, like the O1–O4 labels on the switch. */
export const bulbNumber = (channel: number) => channel + 1;

/** "ALL 4" when every wired bulb is used, otherwise "1 · 2 · 4". */
export function bulbSummary(channels: readonly number[], wired: readonly number[]): string {
  if (channels.length === wired.length && wired.every((c) => channels.includes(c))) {
    return `ALL ${wired.length}`;
  }
  return [...channels]
    .sort((a, b) => a - b)
    .map(bulbNumber)
    .join(' · ');
}

export const WARDS: readonly Ward[] = [
  { code: '2A', name: 'General Medicine', beds: 12, lastCycle: 'TODAY 06:12' },
  { code: '3B', name: 'Surgical Recovery', beds: 12, lastCycle: 'YESTERDAY 22:40' },
  { code: '4C', name: 'Intensive Care', beds: 8, lastCycle: 'TODAY 04:55' },
  { code: '5A', name: 'Maternity', beds: 10, lastCycle: 'YESTERDAY 19:03' },
  { code: '6D', name: 'Oncology', beds: 12, lastCycle: 'TODAY 02:31' },
  { code: 'ER', name: 'Emergency Dept', beds: 14, lastCycle: 'TODAY 07:48' },
];

export const ROBOT_POSITIONS: readonly RobotPosition[] = [
  { id: 'CENTER', label: 'ROOM CENTER', x: 50, y: 50 },
  { id: 'NW', label: 'CORNER NW', x: 22, y: 20 },
  { id: 'NE', label: 'CORNER NE', x: 80, y: 16 },
  { id: 'SW', label: 'CORNER SW', x: 16, y: 84 },
  { id: 'SE', label: 'CORNER SE', x: 84, y: 82 },
];

export interface TimedCheck {
  /** Seconds into the phase at which the check reports OK. */
  readonly at: number;
  readonly label: string;
  readonly detail?: string;
}

export const BOOT_CHECKS: readonly TimedCheck[] = [
  { at: 2.3, label: 'POWER BUS 24V', detail: 'Power Check' },
  { at: 2.9, label: 'KERNEL 4.2.1', detail: 'System Software' },
  { at: 3.5, label: 'DOSIMETRY MODULE', detail: 'UV-C Light Sensors' },
  { at: 4.1, label: 'RF SUBSYSTEM 5.8GHZ', detail: 'Wireless Connection' },
  { at: 4.7, label: 'UI SHELL' },
];

export const LINK_CHECKS: readonly TimedCheck[] = [
  { at: 0.5, label: 'RF HANDSHAKE' },
  { at: 1.3, label: 'TELEMETRY STREAM' },
  { at: 2.1, label: 'LAMP ARRAY DIAGNOSTICS' },
  { at: 2.9, label: 'DOSIMETRY CALIBRATION' },
  { at: 3.7, label: 'LINK SECURE' },
];

export const WORKFLOW_STEPS = ['AUTH', 'LINK', 'ZONE', 'UNIT', 'POS', 'CYCLE', 'REPORT'] as const;

export const SCREEN_STEP: Readonly<Record<Screen, number>> = {
  boot: 0,
  login: 0,
  welcome: 0,
  connect: 1,
  ward: 2,
  bed: 3,
  position: 4,
  countdown: 5,
  cycle: 5,
  aborted: 5,
  report: 6,
};

export const ABORT_REASON_LABEL: Readonly<Record<AbortReason, string>> = {
  MANUAL_EMERGENCY_STOP: 'MANUAL EMERGENCY STOP',
  OCCUPANCY_BREACH: 'MOTION DETECTED — OCCUPANCY BREACH',
  DEVICE_FAULT: 'DEVICE FAULT — MACHINE NOT RESPONDING',
  LAMP_INTERRUPTED: 'MACHINE SWITCHED OFF OUTSIDE EVESIX',
};

/** The aborted screen's heading: says what happened, not only that the cycle stopped. */
export const ABORT_REASON_TITLE: Readonly<Record<AbortReason, string>> = {
  MANUAL_EMERGENCY_STOP: 'Emergency Stop Activated',
  OCCUPANCY_BREACH: 'Motion Detected',
  DEVICE_FAULT: 'Lamp Control Fault',
  LAMP_INTERRUPTED: 'Lamp Switched Off',
};
